import { type CSSProperties, type ReactNode } from 'react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { AppBar } from './AppBar';
import { BottomNav, ALTURA_BARRA_INFERIOR } from './BottomNav';
import { PuxarParaAtualizar } from './PuxarParaAtualizar';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { useAppShell } from '@/hooks/useAppShell';

interface LayoutProps {
  children: ReactNode;
  breadcrumbs?: { label: string; path?: string }[];
  // Esconde a barra de topo global — a página fica full-bleed e cuida dos próprios
  // controles (ex.: a Central de Atendimento, com toggle e sino flutuantes).
  hideHeader?: boolean;
  // Esconde a barra inferior do app (ex.: conversa aberta, que ocupa a tela).
  hideBottomNav?: boolean;
  // Título da barra de topo do app; sem isso usa o nome da tela no menu.
  title?: string;
  // Abre com o menu lateral só nos ícones quando a tela não é larga (tablet,
  // notebook pequeno), para sobrar espaço (ex.: lista + conversa na Central).
  // Não mexe na preferência salva: as outras telas seguem como estavam.
  recolherMenu?: boolean;
}

const LARGURA_MENU_ABERTO = 1440;

export function Layout({ children, hideHeader, hideBottomNav, title, recolherMenu }: LayoutProps) {
  const app = useAppShell();
  const defaultOpen =
    typeof document === 'undefined'
      ? true
      : recolherMenu && window.innerWidth < LARGURA_MENU_ABERTO
        ? false
        : !document.cookie.includes('sidebar:state=false');
  const comBarra = app && !hideBottomNav;

  // --app-bottom: espaço que a barra inferior ocupa (0 sem ela). Telas de
  // altura fixa (Central de Atendimento) descontam com calc(100dvh - var(...)).
  const estilo = {
    '--app-bottom': comBarra
      ? `calc(${ALTURA_BARRA_INFERIOR} + env(safe-area-inset-bottom))`
      : '0px',
  } as CSSProperties;

  return (
    <SidebarProvider defaultOpen={defaultOpen}>
      <Sidebar />
      <SidebarInset style={estilo}>
        {!hideHeader && (app ? <AppBar titulo={title} /> : <Header />)}
        <div
          className="flex-1 overflow-x-hidden"
          style={comBarra ? { paddingBottom: 'var(--app-bottom)' } : undefined}
        >
          {children}
        </div>
        {comBarra && <BottomNav />}
        {/* Telas sem a barra de topo (Central de Atendimento) têm listas que
            rolam sozinhas: lá o puxar para atualizar atrapalharia. */}
        {app && !hideHeader && <PuxarParaAtualizar />}
      </SidebarInset>
    </SidebarProvider>
  );
}
